
import React from 'react';
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import type { Application } from '@/types/database.types';
import { RiskQuestion } from './RiskQuestion';

export interface CarrierInfoProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
}

export const CarrierInfo = ({
  register,
  watch,
  setValue
}: CarrierInfoProps) => {
  const wasCancelled = watch('wc_coverage_cancelled') as boolean | undefined;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Workers' Comp Information</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Please provide information about the current workers' compensation insurance.
      </p>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="current_workers_comp_carrier">Current Workers' Comp Carrier</Label>
            <Input
              id="current_workers_comp_carrier"
              {...register('current_workers_comp_carrier')}
              placeholder="Enter carrier name"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="workers_comp_renewal_date">Renewal Date</Label>
            <Input
              id="workers_comp_renewal_date"
              type="date"
              {...register('workers_comp_renewal_date')}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="workers_comp_with_peo" className="cursor-pointer">
            Is Workers' Comp currently through a PEO?
          </Label>
          <Switch
            id="workers_comp_with_peo"
            checked={Boolean(watch('workers_comp_with_peo'))}
            onCheckedChange={(checked) => setValue('workers_comp_with_peo', !!checked)}
          />
        </div>
        
        <RiskQuestion
          id="has_recent_wc_claims"
          label="Has the organization had any Workers' Comp claims in the past 5 years?"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="recent_wc_claims_details"
          detailsPlaceholder="Please describe the claims, including dates, nature of injuries, claim amounts, and current status..."
        />
        
        <RiskQuestion
          id="wc_coverage_cancelled"
          label="Has the Workers' Comp coverage ever been cancelled or non-renewed?"
          register={register}
          setValue={setValue}
          watch={watch}
          detailsField="wc_cancellation_reason"
          detailsPlaceholder="Provide details about the cancellation or non-renewal, including reasons and timeline..."
        />
        
        <div className="space-y-2">
          <Label htmlFor="company_services_description">Provide a detailed description of the organization's services:</Label>
          <Textarea
            id="company_services_description"
            {...register('company_services_description')}
            placeholder="Describe what the organization does"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="physical_operations_description">Describe the physical operations of the organization:</Label>
          <Textarea
            id="physical_operations_description"
            {...register('physical_operations_description')}
            placeholder="Describe the physical aspects of the operations"
          />
        </div>
      </div>
    </div>
  );
};
